import { Country, Language } from '@/types';
import { client } from '@/client';
import { CodeRequest } from '@/auth';

export type PrivateAppLoginRequest = {
  method: 'BANKID_SE' | 'FREJA' | 'BANKID_NO' | 'MITID' | 'FTN';
};

export type PrivateAppLoginStatusResponse = {
  id: string;
} & (
  | {
      status: 'IN_PROGRESS';
      url?: string;
      bankId?: {
        autoStartToken: string;
        qrData: string;
      };
      freja?: {
        qrData: string;
      };
    }
  | {
      status: 'COMPLETED';
      token: string;
      user: {
        name: string;
        email: string | null;
        credits: number;
        termsOfServiceAccepted: boolean;
        hasValidatedEmail: boolean;
      };
    }
  | {
      status: 'FAILED';
      error: string;
    }
);

export enum CallToActionLocation {
  NEW_AUTH_REQUEST = 'NEW_AUTH_REQUEST',
  NEW_SIGN_REQUEST = 'NEW_SIGN_REQUEST',
  LIST_REQUESTS = 'LIST_REQUESTS',
  LOGIN = 'LOGIN',
  AUTH = 'AUTH',
  SIGN = 'SIGN',
}

export type PrivateAppConfig = {
  callToActions: {
    title: string;
    body: string;
    buttonText: string;
    buttonLink: string;
    language: Language;
    location: CallToActionLocation;
  }[];
};

export type PrivateUser = {
  name: string;
  email?: string;
  checksLeft: number;
  termsOfServiceAccepted: boolean;
  hasValidatedEmail: boolean;
};

export type InitCheckoutRequest = {
  amount: number;
  country: Country;
};

export type CreatePrivateAppAuthRequestRequest = {
  // At least one of email and phone must be set
  email?: string;
  phone?: string;
  country: Country;
  name: string;
  subject: string;
  text?: string;
};

export type PrivateAppAuthRequest = {
  id: string;
  type: 'AUTH' | 'SIGN';
  subject: string;
  text?: string;
  status: 'COMPLETE' | 'PENDING';
  requestedAt: number;
  givenName: string;
  registeredName?: string;
  authenticatedAt?: number;
  authenticatedUsing?: 'BANKID' | 'FREJA' | 'BANKID_NO' | 'MITID' | 'FTN';
};

export type PrivateAppAnonymousAuthRequest = {
  authenticated: boolean;
  subject: string;
  recipientName: string;
  requestorName: string;
  signText?: string;
  hash?: string;
};

export type InitPrivateAppAuthRequest = {
  method: 'BANKID_SE' | 'FREJA' | 'BANKID_NO' | 'MITID' | 'FTN';
};

export type PrivateAppAuthStatusResponse = {
  id: string;
} & (
  | {
      status: 'IN_PROGRESS';
      url?: string;
      bankId?: {
        autoStartToken: string;
        qrData: string;
      };
      freja?: {
        qrData: string;
      };
    }
  | {
      status: 'COMPLETED';
    }
  | {
      status: 'FAILED';
      error: string;
    }
);

export async function initPrivateAppLogin(
  request: PrivateAppLoginRequest,
  signal?: AbortSignal,
): Promise<Extract<PrivateAppLoginStatusResponse, { status: 'IN_PROGRESS' }>> {
  return client
    .post(`/private-app/login`, request, { signal })
    .then((response) => response.data);
}

export async function getPrivateAppLoginStatus(
  id: string,
  signal?: AbortSignal,
): Promise<PrivateAppLoginStatusResponse> {
  return client
    .get(`/private-app/login/${encodeURIComponent(id)}`, { signal })
    .then((response) => response.data);
}

export async function completePrivateAppLogin(
  id: string,
  request: CodeRequest,
  signal?: AbortSignal,
): Promise<Extract<PrivateAppLoginStatusResponse, { status: 'COMPLETED' }>> {
  return client
    .post(`/private-app/login/${encodeURIComponent(id)}`, request, { signal })
    .then((response) => response.data);
}

export async function getPrivateAppConfig(
  signal?: AbortSignal,
): Promise<PrivateAppConfig> {
  return client
    .get('/private-app/config', { signal })
    .then((response) => response.data);
}

export async function getOwnPrivateAppUser(
  signal?: AbortSignal,
): Promise<PrivateUser> {
  return client
    .get('/private-app/user/me', { signal })
    .then((response) => response.data);
}

export async function updateOwnPrivateAppUserEmail(
  email: string,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .put('/private-app/user/email', email, {
      headers: {
        'Content-Type': 'text/plain',
      },
      signal,
    })
    .then((response) => response.data);
}

export async function acceptPrivateAppTermsOfServices(
  signal?: AbortSignal,
): Promise<void> {
  return client
    .put('/private-app/user/termsofservice', null, { signal })
    .then((response) => response.data);
}

export async function validatePrivateUserEmail(
  code: string,
  signal?: AbortSignal,
): Promise<void> {
  return client.post('/private-app/email/validate', null, {
    params: { code },
    signal,
  });
}

export async function deleteOwnPrivateAppUser(
  signal?: AbortSignal,
): Promise<void> {
  return client
    .delete('/private-app/user/me', { signal })
    .then((response) => response.data);
}

export async function initPrivateAppCheckout(
  request: InitCheckoutRequest,
  signal?: AbortSignal,
): Promise<string> {
  return client
    .post('/private-app/checkout', request, { signal })
    .then((response) => response.data);
}

export async function getPrivateAppAuthRequests(
  signal?: AbortSignal,
): Promise<PrivateAppAuthRequest[]> {
  return client
    .get(`/private-app/auth-request`, { signal })
    .then((response) => response.data);
}

export async function getPrivateAppAuthRequest(
  id: string,
  signal?: AbortSignal,
): Promise<PrivateAppAnonymousAuthRequest> {
  return client
    .get(`/private-app/auth-request/${encodeURIComponent(id)}`, { signal })
    .then((response) => response.data);
}

export async function createPrivateAppAuthRequest(
  request: CreatePrivateAppAuthRequestRequest,
  signal?: AbortSignal,
): Promise<string> {
  return client
    .post('/private-app/auth-request', request, { signal })
    .then((response) => response.data);
}

export async function cancelPrivateAppAuthRequest(
  id: string,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .delete(`/private-app/auth-request/${id}`, { signal })
    .then((response) => response.data);
}

export async function getPrivateAppAuthRequestSignature(
  id: string,
  signal?: AbortSignal,
): Promise<Blob> {
  return client
    .get(`/private-app/auth-request/${id}/signature`, {
      responseType: 'blob',
      signal,
    })
    .then((response) => response.data);
}

export async function initPrivateAppAuth(
  requestId: string,
  request: InitPrivateAppAuthRequest,
  signal?: AbortSignal,
): Promise<Extract<PrivateAppAuthStatusResponse, { status: 'IN_PROGRESS' }>> {
  return client
    .post(
      `/private-app/auth-request/${encodeURIComponent(requestId)}/auth`,
      request,
      {
        signal,
      },
    )
    .then((response) => response.data);
}

export async function getPrivateAppAuthStatus(
  requestId: string,
  authId: string,
  signal?: AbortSignal,
): Promise<PrivateAppAuthStatusResponse> {
  return client
    .get(
      `/private-app/auth-request/${encodeURIComponent(requestId)}/auth/${encodeURIComponent(authId)}`,
      {
        signal,
      },
    )
    .then((response) => response.data);
}

export async function completePrivateAppAuth(
  requestId: string,
  authId: string,
  request: CodeRequest,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .post(
      `/private-app/auth-request/${encodeURIComponent(requestId)}/auth/${encodeURIComponent(authId)}`,
      request,
      {
        signal,
      },
    )
    .then((response) => response.data);
}
